import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { RuleCard } from './RuleCard';
import { RuleConflictAlert } from './RuleConflictAlert';
import { VisualRuleSimulation } from './VisualRuleSimulation';

interface ProviderRule {
  id: string;
  providerId: string;
  providerName: string;
  ruleLabel: string;
  ruleType: 'ALLOW' | 'DENY';
  entityType: 'CONDITION' | 'INTENT' | 'PROCEDURE';
  canonicalTag: string;
  canonicalTagLabel: string;
  canonicalTagDescription?: string | null;
}

interface Provider {
  id: string;
  name: string;
}

interface ProviderRulesPanelProps {
  provider: Provider | null;
  rules: ProviderRule[];
  isLoading?: boolean;
  onAddRule: () => void;
  onEditRule: (ruleId: string) => void;
  onDeleteRule: (ruleId: string) => void;
  onCopyRules: () => void;
}

export function ProviderRulesPanel({
  provider,
  rules,
  isLoading,
  onAddRule,
  onEditRule,
  onDeleteRule,
  onCopyRules,
}: ProviderRulesPanelProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    CONDITION: true,
    INTENT: true,
    PROCEDURE: true,
  });

  // Group rules by entity type
  const rulesByType = useMemo(() => {
    return {
      CONDITION: rules.filter((r) => r.entityType === 'CONDITION'),
      INTENT: rules.filter((r) => r.entityType === 'INTENT'),
      PROCEDURE: rules.filter((r) => r.entityType === 'PROCEDURE'),
    };
  }, [rules]);

  // Detect conflicts (same tag with both ALLOW and DENY)
  const conflicts = useMemo(() => {
    const tagMap = new Map<
      string,
      { tagLabel: string; allowRules: typeof rules; denyRules: typeof rules }
    >();

    for (const rule of rules) {
      if (!tagMap.has(rule.canonicalTag)) {
        tagMap.set(rule.canonicalTag, {
          tagLabel: rule.canonicalTagLabel,
          allowRules: [],
          denyRules: [],
        });
      }
      const tagData = tagMap.get(rule.canonicalTag);
      if (tagData) {
        if (rule.ruleType === 'ALLOW') {
          tagData.allowRules.push(rule);
        } else {
          tagData.denyRules.push(rule);
        }
      }
    }

    // Find tags with both ALLOW and DENY
    const conflictingTags = [];
    for (const [tagKey, data] of tagMap) {
      if (data.allowRules.length > 0 && data.denyRules.length > 0) {
        conflictingTags.push({
          tagKey,
          tagLabel: data.tagLabel,
          allowRules: data.allowRules,
          denyRules: data.denyRules,
        });
      }
    }

    return conflictingTags;
  }, [rules]);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (!provider) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <div className="text-sm text-gray-500">Loading rules...</div>
        </CardContent>
      </Card>
    );
  }

  const entityTypeConfig = {
    CONDITION: {
      label: 'Conditions',
      icon: '📋',
      description: 'Medical conditions and diagnoses',
    },
    INTENT: {
      label: 'Intents',
      icon: '🎯',
      description: 'Primary appointment goals',
    },
    PROCEDURE: {
      label: 'Procedures',
      icon: '🔬',
      description: 'Specific medical procedures',
    },
  };

  return (
    <>
      {/* Conflict Alert */}
      {conflicts.length > 0 && <RuleConflictAlert conflicts={conflicts} />}

      {/* Rules Content */}
      <div className="space-y-4">
        {/* Empty State */}
        {rules.length === 0 ? (
          <Card className="p-8 text-center">
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">
                No qualification rules configured for {provider.name}
              </p>
              <p className="text-xs text-gray-500">
                Add rules to control which patients this provider can see based on conditions,
                intents, or procedures. Click "Add Rule" above to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Rules grouped by entity type */}
            {(Object.keys(entityTypeConfig) as Array<keyof typeof entityTypeConfig>).map(
              (entityType) => {
                const config = entityTypeConfig[entityType];
                const entityRules = rulesByType[entityType];
                const allowRules = entityRules.filter((r) => r.ruleType === 'ALLOW');
                const denyRules = entityRules.filter((r) => r.ruleType === 'DENY');

                return (
                  <Collapsible
                    key={entityType}
                    open={openSections[entityType]}
                    onOpenChange={() => toggleSection(entityType)}
                  >
                    <Card>
                      <CollapsibleTrigger className="w-full">
                        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{config.icon}</span>
                              <div className="text-left">
                                <CardTitle className="text-base">{config.label}</CardTitle>
                                <CardDescription className="text-xs">
                                  {config.description} • {entityRules.length}{' '}
                                  {entityRules.length === 1 ? 'rule' : 'rules'}
                                </CardDescription>
                              </div>
                            </div>
                            <ChevronDown
                              className={`h-4 w-4 text-gray-500 transition-transform ${
                                openSections[entityType] ? 'transform rotate-180' : ''
                              }`}
                            />
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <CardContent className="pt-0 space-y-3">
                          {entityRules.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-4">
                              No {config.label.toLowerCase()} rules configured
                            </div>
                          ) : (
                            <>
                              {/* ALLOW Rules */}
                              {allowRules.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                    Allow Rules ({allowRules.length})
                                  </h4>
                                  {allowRules.map((rule) => (
                                    <RuleCard
                                      key={rule.id}
                                      rule={rule}
                                      onEdit={onEditRule}
                                      onDelete={onDeleteRule}
                                    />
                                  ))}
                                </div>
                              )}

                              {/* DENY Rules */}
                              {denyRules.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                    Deny Rules ({denyRules.length})
                                  </h4>
                                  {denyRules.map((rule) => (
                                    <RuleCard
                                      key={rule.id}
                                      rule={rule}
                                      onEdit={onEditRule}
                                      onDelete={onDeleteRule}
                                    />
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              }
            )}

            {/* Visual Simulation */}
            <VisualRuleSimulation rules={rules} providerName={provider.name} />
          </>
        )}
      </div>
    </>
  );
}
